import { useCallback, useEffect, useState } from "react";
import { Sparkles, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UploadDropzone } from "@/components/media/UploadDropzone";
import { MediaTable } from "@/components/media/MediaTable";
import { NewJobDialog } from "@/components/media/NewJobDialog";
import { BatchIntake } from "@/components/media/BatchIntake";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState({ open: false, media: null, mediaIds: null });
  const [demoBusy, setDemoBusy] = useState(false);

  const load = useCallback(() => api.get("/media").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const createDemo = async () => {
    setDemoBusy(true);
    try {
      await api.post("/media/demo?seconds=60");
      toast.success("Demo master generated (60 s, 48 kHz / 24-bit stereo)");
      load();
    } catch (e) {
      toast.error("Demo generation failed");
    } finally {
      setDemoBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.source_filename} from the library?`)) return;
    await api.delete(`/media/${m.id}`);
    setSelected((s) => s.filter((x) => x !== m.id));
    load();
  };

  return (
    <div data-testid="media-library-view" className="space-y-4 fade-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Media Library</h1>
          <p className="text-sm text-slate-400 mt-1">Finished masters, technical metadata and separation entry point.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="batch-separate-button" disabled={!selected.length} onClick={() => setDialog({ open: true, media: null, mediaIds: selected })}
            className="flex items-center gap-2 px-3 py-2 rounded-sm border border-[#3A4A68] text-sm disabled:opacity-40 hover:bg-[#1E2738] transition-colors">
            <Layers size={14} /> Batch separate {selected.length ? `(${selected.length})` : ""}
          </button>
          <button data-testid="generate-demo-master-button" onClick={createDemo} disabled={demoBusy}
            className="flex items-center gap-2 px-3 py-2 rounded-sm bg-[#1E2738] hover:bg-[#253044] text-sm transition-colors disabled:opacity-50">
            <Sparkles size={14} className="text-cyan-400" /> {demoBusy ? "Generating…" : "Generate demo master"}
          </button>
        </div>
      </div>
      <UploadDropzone onUploaded={load} />
      <BatchIntake onDone={load} />
      <MediaTable items={items} selected={selected} onToggle={(id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
        onSeparate={(m) => setDialog({ open: true, media: m, mediaIds: null })} onDelete={remove} />
      <NewJobDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} media={dialog.media} mediaIds={dialog.mediaIds}
        onCreated={() => { setSelected([]); load(); }} />
    </div>
  );
}
